import { Component, Input, OnInit, ViewChild, ViewChildren, SimpleChanges } from "@angular/core";
import { GraphUser } from "src/app/models/graph-user.model";
import _ from "lodash";
import { FormControl } from "@angular/forms";
import { ErrorDialogComponent } from "src/app/error-dialog/error-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { DataService } from "src/app/services/data.service";
import { MatAutocompleteTrigger } from "@angular/material/autocomplete";

@Component({
  selector: "app-real-estate-project-approver",
  templateUrl: "./real-estate-project-approver.component.html",
  styleUrls: ["./real-estate-project-approver.component.scss"],
})
export class RealEstateProjectApproverComponent implements OnInit {
  @Input() approverList: any[] = [];
  @Input() doaAmounts: number[] = [];
  @Input() reviewMode: boolean = false;

  // Tracks, per doaAmounts index, whether the last DOA lookup failed (so a stale/cached 0 isn't mistaken for a real amount).
  doaLookupFailed: boolean[] = [];
  // Tracks whether a lookup (including its automatic retries) is still in flight, so the UI doesn't
  // prematurely clear the failed state and flash a stale amount before the retries finish.
  doaLookupLoading: boolean[] = [];
  private readonly DOA_FAILURE_LOG_KEY = "doaFailureLog";

  additionalApprovers: GraphUser[] = [];
  xmlDoc: any;
  parser: DOMParser = new DOMParser();
  additionalApproverControl: FormControl[] = [];
  additionalApproverOptions: GraphUser[][] = [];

  @ViewChildren(MatAutocompleteTrigger) triggers;

  constructor(private dialog: MatDialog, private dataService: DataService) {}

  ngOnInit() {
    this.initializeApprovers();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle approverList changes
    if (changes["approverList"]) {
      this.initializeApprovers();
    }
    
    // Handle reviewMode changes
    if (changes["reviewMode"]) {
      if (this.reviewMode) {
        this.additionalApproverControl.forEach(control => control.disable());
      } else {
        this.additionalApproverControl.forEach(control => control.enable());
      }
    }
  }

  private initializeApprovers() {
    // Force approverList to be an array and handle various input types
    if (!this.approverList) {
      this.approverList = [];
    } else if (typeof this.approverList === 'object' && !Array.isArray(this.approverList)) {
      // If it's an object, convert it to an array or set to empty array
      this.approverList = Object.values(this.approverList).filter(item => item != null) || [];
    } else if (!Array.isArray(this.approverList)) {
      this.approverList = [];
    }

    // Clear existing arrays to prevent duplication
    this.additionalApprovers = [];
    this.additionalApproverControl = [];
    this.additionalApproverOptions = [];

    // Initialize the arrays based on the approverList
    this.approverList.forEach((approver, index) => {
      this.additionalApprovers.push(approver);
      this.additionalApproverControl.push(new FormControl(approver));
      this.additionalApproverOptions.push([]);
    });

    if (!this.doaLookupFailed || this.doaLookupFailed.length !== this.doaAmounts.length) {
      this.doaLookupFailed = this.doaAmounts.map(() => false);
    }
    if (!this.doaLookupLoading || this.doaLookupLoading.length !== this.doaAmounts.length) {
      this.doaLookupLoading = this.doaAmounts.map(() => false);
    }
  }

  autocompleteName(index: number) {
    return "additionalApprover" + index.toString();
  }

  addNewAdditionalApprover() {
    this.additionalApproverControl.push(new FormControl());
    this.additionalApprovers.push(null);
    this.additionalApproverOptions.push([]);
    this.approverList.push("");
    this.doaAmounts.push(0);
    this.doaLookupFailed.push(false);
    this.doaLookupLoading.push(false);
  }

  displayFnAdditionalApprover(value: GraphUser): any | undefined {
    if (!value) {
      return "";
    } else if (value.givenName && value.surname && value.jobTitle) {
      return (
        value.givenName +
        " " +
        value.surname +
        " (" +
        value.mail +
        "), " +
        value.jobTitle
      );
    } else if (value.givenName && value.surname && !value.jobTitle) {
      return value.givenName + " " + value.surname + " (" + value.mail + ")";
    } else if (value.displayName && value.jobTitle) {
      return value.displayName + " (" + value.mail + "), " + value.jobTitle;
    } else if (value.displayName && !value.jobTitle) {
      return value.displayName + " (" + value.mail + ")";
    } else {
      return value;
    }
  }

  doaAmountMet() {
    for (let i = 0; i < this.doaAmounts.length; i++) {
      // TODO: Update this amount when we get the correct threshold
      if (this.doaAmounts[i] > 0) {
        return true;
      }
    }
    return false;
  }

  deleteApprover(index: number) {
    this.additionalApproverControl.splice(index, 1);
    this.additionalApprovers.splice(index, 1);
    this.additionalApproverOptions.splice(index, 1);
    this.approverList.splice(index, 1);
    this.doaAmounts.splice(index + 1, 1);
    this.doaLookupFailed.splice(index + 1, 1);
    this.doaLookupLoading.splice(index + 1, 1);
  }

  searchUsersAdditionalApprover(searchText: any, index: number) {
    this.dataService
      .searchUsers(searchText)
      .subscribe((result) => {
        const users: GraphUser[] = result["value"];
        const filtered = users.filter(
          (user) =>
            (!!user.jobTitle && !user.jobTitle.startsWith("CW-")) ||
            (user.givenName === "Jared" && user.surname === "Kohler")
        );
        this.additionalApproverOptions[index] = _.orderBy(filtered, [
          "surname",
          "givenName",
        ]);
        this.triggers._results[index].openPanel();
      });
  }

  additionalApproverSelected(event: any, index: number) {
    this.getDOA(event.mailNickname, index + 1, event.mail);
    this.approverList[index] = event;
  }

  deleteAdditionalApproverValue(i: number) {
    this.additionalApprovers[i] = "" as any;
  }

  retryDoaLookup(index: number) {
    const approver = this.approverList[index - 1];
    if (!approver || !approver.mailNickname) {
      return;
    }
    this.getDOA(approver.mailNickname, index, approver.mail);
  }

  // Records failed DOA lookups (console + sessionStorage) so recurring failures can be diagnosed later.
  private logDoaFailure(lanID: string, index: number, reason: string, detail: any): void {
    const entry = {
      lanID,
      index,
      reason,
      detail: detail instanceof Error ? detail.message : detail,
      timestamp: new Date().toISOString(),
    };
    console.error("[DOA lookup failed]", entry);
    try {
      const log = JSON.parse(sessionStorage.getItem(this.DOA_FAILURE_LOG_KEY) || "[]");
      log.push(entry);
      // Keep the log bounded so sessionStorage doesn't grow unbounded during a long session.
      sessionStorage.setItem(this.DOA_FAILURE_LOG_KEY, JSON.stringify(log.slice(-25)));
    } catch {
      // best-effort logging only
    }
  }

  private handleDoaFailure(lanID: string, index: number, reason: string, detail: any): void {
    this.doaLookupFailed[index] = true;
    this.doaLookupLoading[index] = false;
    this.doaAmounts[index] = 0;
    this.logDoaFailure(lanID, index, reason, detail);
  }

  getDOA(lanID: string, index: number, email?: string) {
    // Keep any existing failed/retry indicator visible until we have a definitive result,
    // so an in-flight retry (which can take a few seconds due to backoff) doesn't briefly
    // look successful before flipping back to failed.
    this.doaLookupLoading[index] = true;
    let doc = document.implementation.createDocument("", "", null);
    const pi = doc.createProcessingInstruction(
      "xml",
      'version="1.0" encoding="UTF-8"'
    );
    doc.insertBefore(pi, doc.firstChild);
    let elem1 = doc.createElement("soapenv:Envelope");
    elem1.setAttribute(
      "xmlns:soapenv",
      "http://schemas.xmlsoap.org/soap/envelope/"
    );
    elem1.setAttribute("xmlns:web", "http://duke-energy.com/webservices/");
    let elem2 = doc.createElement("soapenv:Header");
    let elem3 = doc.createElement("soapenv:Body");
    let elem4 = doc.createElement("web:GetDoaInformationByLanid");
    let elem5 = doc.createElement("web:lanids");
    let elem6 = doc.createElement("web:string");
    elem6.innerHTML = lanID;
    elem5.appendChild(elem6);
    elem4.appendChild(elem5);
    elem3.appendChild(elem4);
    elem1.appendChild(elem2);
    elem1.appendChild(elem3);
    doc.appendChild(elem1);
    let finalString = doc.documentElement.outerHTML;
    this.dataService.getDOA(finalString).subscribe(
      (result) => {
        this.xmlDoc = this.parser.parseFromString(result, "text/xml");
        if (
          this.xmlDoc.getElementsByTagName("StatusCode")[0].innerHTML ===
          "ERROR"
        ) {
          const statusMsg = this.xmlDoc.getElementsByTagName("StatusCodeMsg")[0].innerHTML;
          this.handleDoaFailure(lanID, index, "SOAP_ERROR", statusMsg);
          let dataStructure = {
            errorType: "Error Retrieving DOA Amount",
            errorDescription: "An error occured when retrieving DOA information. Please click Retry to try again",
          };
          this.dialog.open(ErrorDialogComponent, {
            data: dataStructure,
            disableClose: true,
          });
        } else {
          const standardAmount = Number(
            this.xmlDoc.getElementsByTagName("Amount")[0].innerHTML
          );

          const RE_EXCEPTION_CATEGORIES = ["RE-LAND", "RE-LEASE", "PROJ-PROJFUND", "RE-BUILDING"];
          const exceptionDoas = this.xmlDoc.getElementsByTagName("ExceptionDoa");
          let exceptionAmount = 0;
          for (let i = 0; i < exceptionDoas.length; i++) {
            const category = exceptionDoas[i].getElementsByTagName("Category")[0];
            if (category && RE_EXCEPTION_CATEGORIES.includes(category.textContent)) {
              const amt = Number(exceptionDoas[i].getElementsByTagName("Amount")[0].textContent);
              if (amt > exceptionAmount) {
                exceptionAmount = amt;
              }
            }
          }
          this.doaAmounts[index] = exceptionAmount || standardAmount;
          this.doaLookupFailed[index] = false;
          this.doaLookupLoading[index] = false;
        }
      },
      (error) => {
        this.handleDoaFailure(lanID, index, "HTTP_ERROR", error);
        let dataStructure = {
          errorType: "Error Retrieving DOA Amount",
          errorDescription: "An error occured when retrieving DOA information. Please click Retry to try again",
        };
        this.dialog.open(ErrorDialogComponent, {
          data: dataStructure,
          disableClose: true,
        });
      }
    );
  }
}
